import { useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { BRAND_PRODUCT } from '../../brand';
import { PreferenceMenu } from '../../components/layout/PreferenceMenu';
import { Logo, Spinner, buttonClassName } from '../../components/ui';
import { useT } from '../../i18n';
import type { MessageKey } from '../../i18n/en';
import { gitlabSignInUrl } from './api';

/** GitLab is the only way in: the API redirects back here with ?error=gitlab_<reason> on refusal. */
export const LoginPage = () => {
  const { t } = useT();
  const { next, error: signInError } = useSearch({ from: '/login' });
  const [leavingForGitlab, setLeavingForGitlab] = useState(false);
  const gitlabError = signInError?.startsWith('gitlab_')
    ? t(`auth.gitlab.${signInError.slice('gitlab_'.length)}` as MessageKey)
    : null;

  return (
    <main className="relative flex min-h-full items-center justify-center bg-canvas px-4 py-12">
      {/* Language and appearance have to be reachable before signing in. */}
      <div className="absolute right-4 top-4">
        <PreferenceMenu />
      </div>

      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          {/* Decorative: the product name is written right below it. */}
          <Logo className="mb-3 size-10" decorative />
          <p className="text-[20px] font-semibold tracking-tight text-ink">{BRAND_PRODUCT}</p>
          <p className="mt-1 text-[13px] text-ink-muted">{t('common.appTagline')}</p>
        </div>

        <div className="flex flex-col gap-4 rounded-xl border border-border bg-surface p-5">
          {gitlabError ? (
            <p className="rounded-md bg-fail-soft px-3 py-2 text-[13px] text-fail" role="alert">
              {gitlabError}
            </p>
          ) : null}

          {/* A plain link: the GitLab round trip is a full page navigation that ends on the API. */}
          <a
            href={gitlabSignInUrl(next)}
            className={buttonClassName('primary')}
            aria-busy={leavingForGitlab}
            onClick={() => setLeavingForGitlab(true)}
          >
            {leavingForGitlab ? <Spinner className="size-3.5" /> : null}
            {t('auth.signInWithGitlab')}
          </a>

          <p className="text-center text-[12px] text-ink-muted">{t('auth.gitlabHint')}</p>
        </div>
      </div>
    </main>
  );
};
